import { i18n } from "@/plugin/vue-i18n";
import type { BuilderContext } from "./types";

/**
 * The configuration snapshot shown on the panel's Insights tab.
 *
 * Informational only — an insight never indicates a problem, which is what
 * separates it from a recommendation. Every value already lives on the store,
 * so this is derivation rather than analysis.
 */
export type InsightTone = "neutral" | "warning";

export interface Insight {
  id: string;
  label: string;
  value: string;
  tone: InsightTone;
  /**
   * Surface the row's info control opens. Omitted where no single surface owns
   * the value — the custom-field count is derived from whatever sits on the
   * canvas, so there is nowhere to send the user that they are not already.
   */
  settingsTab?: string;
}

/** Literal locale keys: the repo forbids building them at runtime. */
const labelFor = (id: string): string => {
  switch (id) {
    case "conditional_logic":
      return i18n.global.t("builderValidation.insights.conditionalLogic") as string;
    case "submit_destination":
      return i18n.global.t("builderValidation.insights.submitDestination") as string;
    case "custom_fields":
      return i18n.global.t("builderValidation.insights.customFields") as string;
    case "payment_mode":
      return i18n.global.t("builderValidation.insights.paymentMode") as string;
    case "email_notification":
      return i18n.global.t("builderValidation.insights.emailNotification") as string;
    case "auto_responder":
      return i18n.global.t("builderValidation.insights.autoResponder") as string;
    default:
      return i18n.global.t("builderValidation.insights.quizCategories") as string;
  }
};

const onOff = (value: unknown): string =>
  value === true
    ? (i18n.global.t("builderValidation.insights.on") as string)
    : (i18n.global.t("builderValidation.insights.off") as string);

const submitDestination = (ctx: BuilderContext): string => {
  switch (String(ctx.formAction.actionType ?? "2")) {
    case "1":
      return i18n.global.t("builderValidation.insights.destinationUrl") as string;
    case "3":
      return i18n.global.t("builderValidation.insights.destinationOrder") as string;
    case "4":
      return i18n.global.t("builderValidation.insights.destinationQuizResult") as string;
    default:
      return i18n.global.t("builderValidation.insights.destinationMessage") as string;
  }
};

/**
 * Unique Custom Field ids used by the asset, per the PRD: placing the same
 * field twice counts once, and standard fields never count.
 */
const customFieldCount = (ctx: BuilderContext): number => {
  const ids = new Set<string>();
  ctx.elements.forEach(({ element, elementKey }) => {
    if (!element.custom) return;
    ids.add(element.id || elementKey);
  });
  return ids.size;
};

const conditionalLogicCount = (ctx: BuilderContext): number => {
  const rules = ctx.builderConfig.conditionalLogic;
  return Array.isArray(rules) ? rules.length : 0;
};

const hasPaymentElement = (ctx: BuilderContext): boolean =>
  ctx.elements.some((entry) => entry.element.tag === "payment");

export const buildInsights = (ctx: BuilderContext): Insight[] => {
  const insights: Insight[] = [];

  // Conditional logic is a form and survey surface; the quiz builder has none,
  // so the row would always read "0 rules" and open nothing.
  if (ctx.assetType !== "quiz") {
    insights.push({
      id: "conditional_logic",
      label: labelFor("conditional_logic"),
      value: i18n.global.t("builderValidation.insights.ruleCount", {
        n: conditionalLogicCount(ctx),
      }) as string,
      tone: "neutral",
      settingsTab: "logic",
    });
  }

  insights.push(
    {
      id: "submit_destination",
      label: labelFor("submit_destination"),
      value: submitDestination(ctx),
      tone: "neutral",
      settingsTab: "settings",
    },
    {
      id: "custom_fields",
      label: labelFor("custom_fields"),
      value: String(customFieldCount(ctx)),
      tone: "neutral",
    }
  );

  // Quizzes have no payments surface, so the row would always read "none".
  if (ctx.assetType !== "quiz" && hasPaymentElement(ctx)) {
    const live = ctx.payment.liveModeOn === true;
    insights.push({
      id: "payment_mode",
      label: labelFor("payment_mode"),
      value: live
        ? (i18n.global.t("builderValidation.insights.live") as string)
        : (i18n.global.t("builderValidation.insights.test") as string),
      // Test mode is the one insight that carries a hint of risk: it reads as
      // configured while collecting no money.
      tone: live ? "neutral" : "warning",
      settingsTab: "payments",
    });
  }

  if (ctx.assetType === "quiz") {
    insights.push({
      id: "quiz_categories",
      label: labelFor("quiz_categories"),
      value: String(ctx.categories.length),
      tone: "neutral",
      settingsTab: "results",
    });
  }

  insights.push(
    {
      id: "email_notification",
      label: labelFor("email_notification"),
      value: onOff(ctx.builderConfig.emailNotifications),
      tone: "neutral",
      settingsTab: "notifications",
    },
    {
      id: "auto_responder",
      label: labelFor("auto_responder"),
      value: onOff(ctx.builderConfig.autoResponder),
      tone: "neutral",
      settingsTab: "notifications",
    }
  );

  return insights;
};
